/**
 * Link exchange: the default reason two verified websites talk to each other.
 *
 * Every verified, reachable, non-opted-out site is treated as open to a link
 * exchange unless it says otherwise.
 * Opting out of partnerships (`users.partnershipsOptOut`) removes a site from
 * the candidate pool, so it opts out of this too and needs no second switch.
 *
 * Dependency-free on purpose: the client bundle imports it for proposal copy.
 */

function hostname(url: string): string {
	try {
		return new URL(url).hostname.replace(/^www\./, "");
	} catch {
		return url;
	}
}

/**
 * Add partner-specific referral attribution to the exact destination page.
 * Drop the old default exchange campaign; preserve other campaign values and
 * query parameters. Reapplying the helper is safe.
 */
export function attributedLinkExchangeUrl(
	destinationUrl: string,
	referrerUrl: string,
): string {
	try {
		const destination = new URL(destinationUrl);
		const referrer = new URL(referrerUrl);
		if (
			(destination.protocol !== "http:" && destination.protocol !== "https:") ||
			(referrer.protocol !== "http:" && referrer.protocol !== "https:")
		) {
			return destinationUrl;
		}
		const source = referrer.hostname.replace(/^www\./, "");
		if (!source) return destinationUrl;
		destination.searchParams.set("utm_source", source);
		destination.searchParams.set("utm_medium", "referral");
		if (destination.searchParams.get("utm_campaign") === "link_exchange") {
			destination.searchParams.delete("utm_campaign");
		}
		return destination.toString();
	} catch {
		return destinationUrl;
	}
}

/**
 * The opening message, written for the sender.
 *
 * Deliberately NOT localized: this text is composed into a message sent to
 * another user, and threads cross brands. Same rule the rest of the outbound
 * partnership copy follows.
 *
 * One link each, both editorial, both in context. That is the version of a
 * trade that survives contact with a search engine's spam policy, so it is the
 * only version the product proposes. The sender sees the exact text before
 * sending it.
 */
export function buildExchangeProposal({
	fromUrl,
	fromTitle,
	toUrl,
	toTitle,
	sharedTopic,
}: {
	fromUrl?: string;
	fromTitle?: string;
	toUrl: string;
	toTitle?: string;
	/** Category or audience both sites share, when the matcher found one. */
	sharedTopic?: string;
}): { subject: string; message: string } {
	const fromHost = fromUrl ? hostname(fromUrl) : "my website";
	const fromName = fromTitle?.trim() || fromHost;
	const toHost = hostname(toUrl);
	const toName = toTitle?.trim() || toHost;

	const overlap = sharedTopic
		? `We both write for the same ${sharedTopic} audience, so a reader coming either way lands somewhere useful.`
		: "Our audiences overlap enough that a reader coming either way lands somewhere useful.";

	return {
		subject: `Link exchange between ${fromHost} and ${toHost}`,
		message: [
			`Hi ${toName} team,`,
			"",
			`I run ${fromName} (${fromHost}). ${overlap}`,
			"",
			`Proposal: I add a link to ${toHost} from a relevant page on ${fromHost}, and you add a link to ${fromHost} from a relevant page on ${toHost}. One link each, inside real content, where it actually helps the reader. No link farms and no sitewide footer links.`,
			"",
			`I can go first once we agree on the pages. Tell me which page of ${toHost} you want the link to point at.`,
			"",
			`Thanks,`,
			fromName,
		].join("\n"),
	};
}

/**
 * The opening message for an auto-exchange proposal aimed at a specific target
 * page. Same contract as {@link buildExchangeProposal} (English-only, one
 * editorial link each, sender offers to go first), but the ask names the exact
 * page and hands the partner a ready anchor and topic angle, so accepting takes
 * a decision, not research. Composed deterministically from setup-time cached
 * suggestions: the daily runner must never need a model call.
 */
export function buildTargetedExchangeProposal({
	fromUrl,
	fromTitle,
	toUrl,
	toTitle,
	sharedTopic,
	targetUrl,
	anchorText,
	topicAngle,
}: {
	fromUrl: string;
	fromTitle?: string;
	toUrl: string;
	toTitle?: string;
	/** Category or audience both sites share, when the matcher found one. */
	sharedTopic?: string;
	/** The page on the sender's site that should receive the partner's link. */
	targetUrl: string;
	/** Suggested anchor for the partner's link, from the cached suggestions. */
	anchorText?: string;
	/** One-line article angle the link would sit naturally in. */
	topicAngle?: string;
}): { subject: string; message: string } {
	const fromHost = hostname(fromUrl);
	const fromName = fromTitle?.trim() || fromHost;
	const toHost = hostname(toUrl);
	const toName = toTitle?.trim() || toHost;

	const overlap = sharedTopic
		? `We both write for the same ${sharedTopic} audience, so a reader coming either way lands somewhere useful.`
		: "Our audiences overlap enough that a reader coming either way lands somewhere useful.";

	const ask = [
		`Proposal: you add one link to this page of mine: ${targetUrl}`,
		anchorText ? `A natural anchor would be "${anchorText}".` : null,
		topicAngle
			? `It fits an article about ${topicAngle}, or any existing post of yours on that ground.`
			: null,
	]
		.filter(Boolean)
		.join(" ");

	return {
		subject: `Link exchange between ${fromHost} and ${toHost}`,
		message: [
			`Hi ${toName} team,`,
			"",
			`I run ${fromName} (${fromHost}). ${overlap}`,
			"",
			ask,
			"",
			`In return I add a link to whichever page of ${toHost} you pick, from a relevant page on ${fromHost}. One link each, inside real content, where it actually helps the reader. No link farms and no sitewide footer links.`,
			"",
			`I can go first once we agree on the pages. Tell me which page of ${toHost} you want me to link to.`,
			"",
			`Thanks,`,
			fromName,
		].join("\n"),
	};
}

/** The opening message for a pair that already opted into auto-matching. */
export function buildAutoMatchedExchangeMessage(
	fromHost: string,
	toHost: string,
): string {
	return [
		`Both ${fromHost} and ${toHost} turned on auto-matching for link exchanges, and you fit each other's criteria.`,
		"",
		`The trade: one relevant editorial link each. ${fromHost} links to ${toHost}, and ${toHost} links to ${fromHost}. You can start both link tasks below. Publish your article and paste its URL. Once both links are live, the exchange goes to review.`,
		"",
		"Not a fit after all? Either side can close this exchange, no questions asked.",
	].join("\n");
}
